/**
 * Waitlist Page Tour — one-time coach-mark walkthrough for /dashboard.
 * Same modal pattern as VoiceTour. Auto-opens on first visit, replayable
 * via the "Take a tour" button in the Waitlist page header.
 */
import { useEffect, useState } from "react";
import { X, ArrowLeft, ArrowRight, Sparkle, CheckCircle } from "@phosphor-icons/react";

const TOUR_KEY = "buzzerai-waitlist-tour-v1";

export const hasSeenWaitlistTour = () => {
  try { return localStorage.getItem(TOUR_KEY) === "1"; }
  catch (e) { /* Safari private mode blocks localStorage access — treat as seen */ return true; }
};

export const markWaitlistTourSeen = () => {
  try { localStorage.setItem(TOUR_KEY, "1"); }
  catch (e) { /* Safari private mode blocks localStorage.setItem — silently ignore */ console.debug("waitlist tour mark failed:", e?.message); }
};

const STEPS = [
  {
    title: "Welcome to your waitlist",
    body: "This is the live queue. Guests appear the moment they join — from your public /join page, from the phone AI, or when you add them here yourself. It refreshes every few seconds.",
    emoji: "sparkle",
  },
  {
    title: "1 · Add a guest",
    body: "Click 'Add guest' to log a walk-in or a phone-in caller yourself. Pick the source (Walk-in / Phone / Web) — it shows up in analytics later. Party size and phone are enough to get going.",
  },
  {
    title: "2 · Text ready · Seat · Remove",
    body: "Each waiting row has three actions. 'Text ready' fires the ready-SMS template. 'Seat' marks them seated (frees a table in Smart Wait). 'Remove' clears them off the list without sending anything.",
  },
  {
    title: "3 · Smart Wait panel",
    body: "Enter your table inventory (how many 2-tops, 4-tops, 6-tops you own) and your turn times per party size. Guests on the /join page instantly get personalized wait quotes — 'wait for a party of 4 is ~35 min' — instead of a flat guess.",
  },
  {
    title: "4 · Share your public link",
    body: "Your /join/<slug> link goes on your door, on the printed QR poster, or in your Google Business profile. Guests join in one tap, no downloads. Copy it from Settings any time.",
  },
  {
    title: "You're ready!",
    body: "Any time you want to see this tour again, click 'Take a tour' at the top-right of the Waitlist page. Questions? Hit the Support button in the bottom corner.",
    emoji: "check",
  },
];

export default function WaitlistTour({ open, onClose }) {
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (open) setStep(0);
  }, [open]);

  if (!open) return null;

  const s = STEPS[step];
  const isFirst = step === 0;
  const isLast = step === STEPS.length - 1;

  const close = () => {
    markWaitlistTourSeen();
    onClose();
  };

  const next = () => {
    if (isLast) return close();
    setStep(step + 1);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#121914]/50 backdrop-blur-sm flex items-center justify-center p-4"
      data-testid="waitlist-tour"
      onClick={close}
    >
      <div
        className="bg-white w-full max-w-md rounded-2xl border border-[#E1DFD5] shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 border-b border-[#E1DFD5] flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#1E3A2F] flex items-center justify-center">
            {s.emoji === "check" ? (
              <CheckCircle size={20} color="#fff" weight="fill" />
            ) : (
              <Sparkle size={20} color="#fff" weight="fill" />
            )}
          </div>
          <div className="flex-1">
            <div className="overline text-[#5C6B61]">Waitlist tour · {step + 1} of {STEPS.length}</div>
            <div className="font-heading font-extrabold text-lg leading-tight" data-testid="waitlist-tour-title">{s.title}</div>
          </div>
          <button
            type="button"
            onClick={close}
            className="w-9 h-9 rounded-full hover:bg-[#F1D7D2] flex items-center justify-center"
            data-testid="waitlist-tour-close"
            aria-label="Skip tour"
          >
            <X size={16} />
          </button>
        </div>

        <div className="p-5">
          <p className="text-sm text-[#121914] leading-relaxed" data-testid="waitlist-tour-body">{s.body}</p>
        </div>

        <div className="px-5 pb-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-1.5" data-testid="waitlist-tour-progress">
            {STEPS.map((s, i) => (
              <span
                key={s.title}
                className={`w-1.5 h-1.5 rounded-full transition-colors ${i === step ? "bg-[#1E3A2F] w-4" : i < step ? "bg-[#1E3A2F]/50" : "bg-[#E1DFD5]"}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {!isFirst && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="btn-ghost !py-2 !px-3 text-sm inline-flex items-center gap-1"
                data-testid="waitlist-tour-back"
              >
                <ArrowLeft size={14} /> Back
              </button>
            )}
            <button
              type="button"
              onClick={next}
              className="btn-primary !py-2 !px-4 text-sm inline-flex items-center gap-1"
              data-testid="waitlist-tour-next"
            >
              {isLast ? "Got it" : "Next"} {!isLast && <ArrowRight size={14} color="#fff" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
